import classNames from "classnames";
import dateFormat from "dateformat";
import { GraphQLError } from "graphql";
import PropTypes from "prop-types";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { Service, Stay } from "rsl-api-106/lib/types";

import Alert from "@components/_Layout/Alert";
import Button from "@components/_Layout/Controls/Button";
import InputPassword from "@components/_Layout/Controls/InputPassword";
import InputText from "@components/_Layout/Controls/InputText";
import Popup from "@components/_Layout/Controls/Popup";
import { useAuth, useCart, useSession } from "@hooks";
import { sendDataLayer, trackClick } from "@utils/tracking/gtm";

import ForgetPassword from "../ForgetPassword";

type LoginProps = {
  onLoginSuccess?: (service: Service | null) => void;
  centeredActions?: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_to_log_in", //Se connecter
  "LANG_neobooking_password", //Mot de passe,
  "LANG_neobooking_login_in_progess", //Connexion en cours,
  "LANG_neobooking_forgot_password", //Mot de passe oublié ?,
  "LANG_neobooking_email_address", //Adresse email
  "LANG_neobooking_you_are_now_logged", //Vous êtes maintenant connecté !
  "LANG_neobooking_error_while_login", //Erreur lors de la connexion
];

const Login: React.FC<LoginProps> = ({
  onLoginSuccess,
  centeredActions = false,
}) => {
  /* Hooks */
  const { sessionId, getLanguageVarValue, lang } = useSession();
  const { loginAnyclientType } = useAuth();
  const { cart } = useCart();

  /* State */
  const [password, setPassword] = useState<string>("");
  const [username, setUsername] = useState<string>("");

  /* Submit / Error State */
  const [responseError, setResponseError] = useState<GraphQLError>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sessionId) {
      trackClick(
        "Tunnel - Connexion",
        "click bouton",
        "Bouton - Se connecter",
        ""
      );
      const toastLogin = toast.loading(
        getLanguageVarValue("LANG_neobooking_login_in_progess")
      );
      setIsSubmitting(true);
      setResponseError(undefined);
      loginAnyclientType(username, password, (service, err) => {
        if (err) {
          toast.error(err.message, {
            id: toastLogin,
          });
          setResponseError(err);
        } else {
          toast.success(
            getLanguageVarValue("LANG_neobooking_you_are_now_logged"),
            {
              id: toastLogin,
            }
          );

          const items = handleGetGTMItems();
          const totalOccupant = handleGetTotalOccupant();
          const duration = handleGetTotalDuration();

          let dateArrivee = cart?.reservations!![0]?.startDate;
          if (!dateArrivee) {
            dateArrivee = new Date();
          } else {
            dateArrivee = new Date(dateArrivee);
          }

          let dateDepart = cart?.reservations!![0]?.endDate;
          if (!dateDepart) {
            dateDepart = new Date();
          } else {
            dateDepart = new Date(dateDepart);
          }

          onLoginSuccess && onLoginSuccess(service);
        }
        setIsSubmitting(false);
      });
    }
  };

  const handleGetTotalOccupant = () => {
    let n = 0;
    cart?.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        n += s?.occupants?.length ?? 0;
      });
    });

    return n;
  };

  const handleGetTotalDuration = () => {
    let n = 0;
    cart?.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        n += s?.nbDays ?? 0;
      });
    });

    return n;
  };

  const handleGetNbRoom = (stay: Stay) => {
    let n = 0;
    stay?.distribution?.roomTypes?.forEach((r) => {
      n += r?.quantity ?? 0;
    });

    return n;
  };

  const getFormuleSejour = (stay: Stay) => {
    let s = "";
    stay.occupants?.forEach((o) => {
      o?.lineItems?.forEach((l) => {
        if (l?.product?.isBase) {
          s = l.product.label!!;
        }
      });
    });

    return s;
  };

  const handleGetGTMItems = () => {
    let items: any = [];
    cart?.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        items = [
          ...items,
          {
            currency: s?.totalAmount?.currencyCode,
            item_id: r.property?.id,
            item_name: r.property?.label,
            item_category: s?.distribution?.roomTypes
              ?.map((x) => x?.code)
              .join(","),
            item_category2: "VACANCEOLE location de particulier", //ou location de particulier ou partenaire
            item_brand: "Vacanceole",
            item_variant: getFormuleSejour(s!!), //Formule location – Studio 2 personnes - Twin
            price: s?.totalAmount?.amount?.toFixed(2),
            quantity: handleGetNbRoom(s!!), //ATTENTION à DYNAMISER EN FONCTION DU CHOIX
          },
        ];
      });
    });

    return items;
  };

  const handleClickForgotPassword = (popupClickHandler: () => void) => {
    popupClickHandler();
    trackClick(
      "Tunnel - Connexion",
      "click bouton",
      "Bouton - Mot de passe oublié",
      ""
    );
  };

  return (
    <>
      <div className="tw-flex">
        <div className="tw-w-full">
          <form
            onSubmit={handleSubmitLogin}
            onChange={() => setResponseError(undefined)}
          >
            <div className="tw-grid tw-grid-cols-2 tw-gap-6 tw-my-4">
              <div className="tw-col-span-2">
                <InputText
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_email_address"
                  )}
                  required
                  aria-required="true"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-2">
                <InputPassword
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  additionalClassname="tw-w-full"
                  placeholder={getLanguageVarValue("LANG_neobooking_password")}
                />
              </div>
            </div>
            {responseError && (
              <>
                <div className="tw-mb-4">
                  <Alert
                    dismissable={false}
                    title={`${getLanguageVarValue(
                      "LANG_neobooking_error_while_login"
                    )}:`}
                    variant="danger"
                  >
                    {responseError.message}
                  </Alert>
                </div>
              </>
            )}
            <div
              className={classNames(
                "tw-flex",
                centeredActions ? "tw-justify-center" : "tw-justify-end"
              )}
            >
              <Button
                isLoading={isSubmitting}
                loadingChildren={
                  <>
                    {getLanguageVarValue("LANG_neobooking_login_in_progess")}...
                  </>
                }
                type="submit"
                variant="gradient"
              >
                {getLanguageVarValue("LANG_neobooking_to_log_in")}
              </Button>
            </div>
          </form>
          <div
            className={classNames(
              "tw-flex tw-flex-wrap tw-gap-2 tw-mt-2",
              centeredActions && "tw-justify-center"
            )}
          >
            <Popup
              btnElement={(onClick) => (
                <Button
                  onClick={() => handleClickForgotPassword(onClick)}
                  additionalClassname="tw-leading-none"
                  shape="link"
                  type="button"
                  variant="black"
                  size="sm"
                >
                  {getLanguageVarValue("LANG_neobooking_forgot_password")}
                </Button>
              )}
              showBtnClose={false}
            >
              <ForgetPassword defaultEmail={username} />
            </Popup>
          </div>
        </div>
      </div>
    </>
  );
};

type LoginPartnerProps = {};

export const LoginPartner: React.FC<LoginPartnerProps> = () => {
  /* Hooks */
  const { sessionId, getLanguageVarValue } = useSession();
  const { partnerLogin } = useAuth();

  /* State */
  const [password, setPassword] = useState<string>("");
  const [username, setUsername] = useState<string>("");

  /* Submit / Error State */
  const [responseError, setResponseError] = useState<GraphQLError>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  /* Functions */
  const handleSubmitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sessionId) {
      const toastLogin = toast.loading(
        getLanguageVarValue("LANG_neobooking_login_in_progess")
      );
      setIsSubmitting(true);
      setResponseError(undefined);
      partnerLogin(username, password, (err) => {
        if (err) {
          toast.error(err.message, {
            id: toastLogin,
          });
          setResponseError(err);
        } else {
          toast.success(
            getLanguageVarValue("LANG_neobooking_you_are_now_logged"),
            {
              id: toastLogin,
            }
          );
        }
        setIsSubmitting(false);
      });
    }
  };

  /* Render */
  return (
    <div className="tw-flex">
      <div className="tw-w-full">
        <form
          onSubmit={handleSubmitLogin}
          onChange={() => setResponseError(undefined)}
        >
          <div className="tw-grid tw-grid-cols-2 tw-gap-6 tw-my-4">
            <div className="tw-col-span-2">
              <InputText
                placeholder={getLanguageVarValue("LANG_neobooking_user_login")}
                required
                aria-required="true"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                additionalClassname="tw-w-full"
                type="text"
              />
            </div>
            <div className="tw-col-span-2">
              <InputPassword
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                additionalClassname="tw-w-full"
                placeholder={getLanguageVarValue("LANG_neobooking_password")}
              />
            </div>
          </div>
          {responseError && (
            <>
              <div className="tw-mb-4">
                <Alert
                  dismissable={false}
                  title={`${getLanguageVarValue(
                    "LANG_neobooking_error_while_login"
                  )}:`}
                  variant="danger"
                >
                  {responseError.message}
                </Alert>
              </div>
            </>
          )}
          <div
            className={classNames(
              "tw-flex"
              //centeredActions ? "tw-justify-center" : "tw-justify-end"
            )}
          >
            <Button
              isLoading={isSubmitting}
              loadingChildren={
                <>
                  {getLanguageVarValue("LANG_neobooking_login_in_progess")}...
                </>
              }
              type="submit"
              variant="gradient"
            >
              {getLanguageVarValue("LANG_neobooking_to_log_in")}
            </Button>
          </div>
        </form>
        <Popup
          btnElement={(onClick) => (
            <Button
              onClick={onClick}
              shape="link"
              type="button"
              variant="black"
              additionalClassname="tw-mt-3"
              size="sm"
            >
              {getLanguageVarValue("LANG_neobooking_forgot_password")}
            </Button>
          )}
        >
          <ForgetPassword defaultEmail="" />
        </Popup>
      </div>
    </div>
  );
};

Login.propTypes = {
  //
};

export default Login;
